import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'react-toastify';
import Button from '../components/Button';
import MaintenanceTasksPanel from '../components/MaintenanceTasksPanel';

interface Photo {
  id: string;
  s3Key: string;
  order: number;
  isPrimary: boolean;
}

interface Listing {
  _id: string;
  name: string;
  title: string;
  description: string;
  status: string;
  ownerId: string;
  adminDisabled: boolean;
  photos?: Photo[];
  createdAt: string;
}

interface User {
  _id: string;
  cognitoId: string;
  email: string;
  displayName?: string;
  isOwner: boolean;
  isAdmin: boolean;
  disabled: boolean;
  createdAt: string;
}

interface Rental {
  _id: string;
  listingId: {
    _id: string;
    title: string;
    name: string;
  };
  renterId?: string;
  renterName: string;
  renterEmail: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  startDateTime: string;
  endDateTime: string;
  price: number;
  createdAt: string;
  cancelledBy?: 'owner' | 'renter' | 'admin';
  cancellationReason?: string;
}

type TabType = 'listings' | 'users' | 'rentals' | 'utilities';

const AdminDashboardPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('listings');
  const [listings, setListings] = useState<Listing[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [rentals, setRentals] = useState<Rental[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchListings = useCallback(async () => {
    try {
      const data = await api.get<{ listings: Listing[] }>('/listings/admin/all');
      setListings(data.listings);
    } catch {
      toast.error('Failed to load listings');
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await api.get<{ users: User[] }>('/users/all');
      setUsers(data.users);
    } catch {
      toast.error('Failed to load users');
    }
  }, []);

  const fetchRentals = useCallback(async () => {
    try {
      const data = await api.get<{ rentals: Rental[] }>('/rentals');
      setRentals(data.rentals);
    } catch {
      toast.error('Failed to load rentals');
    }
  }, []);

  const loadTabData = useCallback(async () => {
    setIsLoading(true);
    try {
      switch (activeTab) {
        case 'listings':
          await fetchListings();
          break;
        case 'users':
          await fetchUsers();
          break;
        case 'rentals':
          await fetchRentals();
          break;
      }
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, fetchListings, fetchUsers, fetchRentals]);

  useEffect(() => {
    loadTabData();
  }, [loadTabData]);

  const handleDisable = async (listingId: string) => {
    if (!window.confirm('Are you sure you want to disable this listing? All confirmed rentals will be cancelled.')) {
      return;
    }

    setActionLoading(listingId);
    try {
      await api.post(`/listings/admin/${listingId}/disable`);
      toast.success('Listing disabled successfully');
      fetchListings();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err?.response?.data?.error || 'Failed to disable listing');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEnable = async (listingId: string) => {
    setActionLoading(listingId);
    try {
      await api.post(`/listings/admin/${listingId}/enable`);
      toast.success('Listing enabled successfully');
      fetchListings();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err?.response?.data?.error || 'Failed to enable listing');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisableUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to disable this user? All their rentals will be cancelled and refunded.')) {
      return;
    }

    setActionLoading(userId);
    try {
      await api.post(`/users/${userId}/disable`);
      toast.success('User disabled successfully');
      fetchUsers();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err?.response?.data?.error || 'Failed to disable user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEnableUser = async (userId: string) => {
    setActionLoading(userId);
    try {
      await api.post(`/users/${userId}/enable`);
      toast.success('User enabled successfully');
      fetchUsers();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err?.response?.data?.error || 'Failed to enable user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelRental = async (rentalId: string) => {
    if (!window.confirm('Are you sure you want to cancel this rental? The renter will be refunded.')) {
      return;
    }

    setActionLoading(rentalId);
    try {
      await api.post(`/rentals/${rentalId}/cancel/admin`);
      toast.success('Rental cancelled successfully');
      fetchRentals();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err?.response?.data?.error || 'Failed to cancel rental');
    } finally {
      setActionLoading(null);
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Admin Dashboard</h1>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('listings')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'listings'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            Listings
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'users'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            Users
          </button>
          <button
            onClick={() => setActiveTab('rentals')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'rentals'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            Rentals
          </button>
          <button
            onClick={() => setActiveTab('utilities')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'utilities'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            Utilities
          </button>
        </nav>
      </div>

      {isLoading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <>
          {/* Listings Tab */}
          {activeTab === 'listings' && (
            <>
              {listings.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                  <p className="text-gray-600">No listings found.</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Listing
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Owner ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {listings.map((listing) => (
                          <tr key={listing._id} className={listing.adminDisabled ? 'bg-red-50' : ''}>
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{listing.title}</div>
                                  <div className="text-sm text-gray-500">{listing.name}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900 font-mono">{listing.ownerId.substring(0, 8)}...</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="space-y-1">
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    listing.status === 'active'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {listing.status}
                                </span>
                                {listing.adminDisabled && (
                                  <div>
                                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                      Admin Disabled
                                    </span>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {new Date(listing.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end gap-2">
                                <Link to={`/${listing.name}`} target="_blank">
                                  <Button size="sm" variant="secondary">
                                    View
                                  </Button>
                                </Link>
                                {listing.adminDisabled ? (
                                  <Button
                                    size="sm"
                                    onClick={() => handleEnable(listing._id)}
                                    isLoading={actionLoading === listing._id}
                                  >
                                    Enable
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => handleDisable(listing._id)}
                                    isLoading={actionLoading === listing._id}
                                  >
                                    Disable
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <>
              {users.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                  <p className="text-gray-600">No users found.</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            User
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Roles
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {users.map((user) => (
                          <tr key={user._id} className={user.disabled ? 'bg-red-50' : ''}>
                            <td className="px-6 py-4">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {user.displayName || user.email}
                                </div>
                                <div className="text-sm text-gray-500">{user.email}</div>
                                <div className="text-xs text-gray-400 font-mono">{user.cognitoId.substring(0, 12)}...</div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex gap-1 flex-wrap">
                                {user.isAdmin && (
                                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                                    Admin
                                  </span>
                                )}
                                {user.isOwner && (
                                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                    Owner
                                  </span>
                                )}
                                {!user.isAdmin && !user.isOwner && (
                                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                    Renter
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {user.disabled ? (
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                  Disabled
                                </span>
                              ) : (
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                  Active
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {new Date(user.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end gap-2">
                                {user.disabled ? (
                                  <Button
                                    size="sm"
                                    onClick={() => handleEnableUser(user._id)}
                                    isLoading={actionLoading === user._id}
                                  >
                                    Enable
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="danger"
                                    onClick={() => handleDisableUser(user._id)}
                                    isLoading={actionLoading === user._id}
                                    disabled={user.isAdmin}
                                  >
                                    {user.isAdmin ? 'Cannot Disable Admin' : 'Disable'}
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Rentals Tab */}
          {activeTab === 'rentals' && (
            <>
              {rentals.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                  <p className="text-gray-600">No rentals found.</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Listing
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Renter
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Dates
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Price
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {rentals.map((rental) => (
                          <tr key={rental._id} className={rental.status === 'cancelled' ? 'bg-gray-50' : ''}>
                            <td className="px-6 py-4">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{rental.listingId.title}</div>
                                <div className="text-sm text-gray-500">{rental.listingId.name}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{rental.renterName}</div>
                                <div className="text-sm text-gray-500">{rental.renterEmail}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">
                                {new Date(rental.startDateTime).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                              </div>
                              <div className="text-sm text-gray-500">
                                to {new Date(rental.endDateTime).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  rental.status === 'confirmed'
                                    ? 'bg-green-100 text-green-800'
                                    : rental.status === 'cancelled'
                                    ? 'bg-red-100 text-red-800'
                                    : rental.status === 'completed'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}
                              >
                                {rental.status}
                              </span>
                              {rental.status === 'cancelled' && rental.cancelledBy && (
                                <div className="text-xs text-gray-500 mt-1">
                                  By: {rental.cancelledBy}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">${rental.price.toFixed(2)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end gap-2">
                                {rental.status === 'confirmed' && (
                                  <Button
                                    size="sm"
                                    variant="danger"
                                    onClick={() => handleCancelRental(rental._id)}
                                    isLoading={actionLoading === rental._id}
                                  >
                                    Cancel
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Utilities Tab */}
          {activeTab === 'utilities' && <MaintenanceTasksPanel />}
        </>
      )}
    </div>
  );
};

export default AdminDashboardPage;

